import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ChevronsLeft, ChevronsRight, Columns, LayoutGrid } from '../../../../icons';
import { usePreviewNavStore } from '../../../../lib/preview-nav';
import { useChatPreviewStore } from '../../../../store/chatPreviewStore';
import { tChat } from '../../../../lib/chat-i18n';

/**
 * PreviewThumbRail (chat-doc-preview-rail — 2026-08-11) — 페이지/슬라이드 썸네일 레일.
 *
 * 문서 본문 좌(기본) 또는 우에 붙는다. 기본은 **닫힘** 상태로, 접힌 막대의 버튼을 눌러 펼친다.
 * 헤더에 좌↔우 배치 전환과 접기 버튼이 있고, 썸네일을 누르면 해당 페이지로 이동한다.
 *
 * 썸네일을 실제로 그리는 주체는 포맷별 렌더러다 — 레일은 preview-nav 레지스트리에 등록된
 * `drawThumb`/`goTo` 만 호출한다(렌더러 구조 무변경).
 *
 * 키보드 이동 (2026-08-12 사용자 요청): 레일에 포커스가 있을 때 ↓/↑(= PageDown/PageUp) 으로
 * 다음·이전 페이지, Home/End 로 처음·끝으로 이동한다. 이동한 페이지의 썸네일은 레일 안에서
 * 자동으로 보이는 위치까지 스크롤된다.
 */
export function PreviewThumbRail() {
  const source = usePreviewNavStore((s) => s.source);
  const activeIndex = usePreviewNavStore((s) => s.activeIndex);
  const railOpen = useChatPreviewStore((s) => s.railOpen);
  const railSide = useChatPreviewStore((s) => s.railSide);
  const toggleRail = useChatPreviewStore((s) => s.toggleRail);
  const setRailSide = useChatPreviewStore((s) => s.setRailSide);
  const listRef = useRef<HTMLDivElement>(null);

  // ↓/↑ 로 페이지 이동. 조기 return(아래) 보다 위에서 선언해야 훅 순서가 깨지지 않는다.
  const onListKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const src = source;
      if (!src || src.count <= 1) return;
      const last = src.count - 1;
      const cur = Math.min(last, Math.max(0, activeIndex));
      let next: number;
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
        case 'PageDown':
          next = Math.min(last, cur + 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
        case 'PageUp':
          next = Math.max(0, cur - 1);
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      // 레일이 본문 스크롤을 함께 먹지 않도록 기본 동작을 막는다(마지막/처음이라 값이 같아도).
      e.preventDefault();
      if (next !== cur) src.goTo(next);
    },
    [source, activeIndex],
  );

  /**
   * 활성 썸네일을 레일 안에서 보이게 유지 — 키보드로 계속 내려가도 레일이 따라 움직인다.
   * 본문 쪽 스크롤에 이끌려 activeIndex 가 바뀐 경우에도 같은 처리로 자연스럽게 맞춰진다.
   * (jsdom 등 scrollIntoView 미구현 환경을 위해 존재 확인 후 호출.)
   */
  useEffect(() => {
    if (!railOpen) return;
    const el = listRef.current?.querySelector('.chat-doc-preview-thumb.is-active');
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
      // 키보드 이동 중이면 포커스도 따라가게 해 다음 키 입력이 이어진다.
      const list = listRef.current;
      if (list && list.contains(document.activeElement) && document.activeElement !== el) el.focus();
    }
  }, [activeIndex, railOpen]);

  // 페이지가 하나뿐이면 레일이 의미 없다(등록 전에도 숨김).
  if (!source || source.count <= 1) return null;

  const collapseIcon = railSide === 'left' ? <ChevronsLeft size={14} /> : <ChevronsRight size={14} />;
  const expandIcon = railSide === 'left' ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />;

  if (!railOpen) {
    return (
      <div className={`chat-doc-preview-rail is-collapsed is-${railSide}`}>
        <button
          type="button"
          className="chat-doc-preview-rail-toggle"
          onClick={toggleRail}
          aria-expanded={false}
          aria-label={tChat('chat.preview.rail_open')}
          title={tChat('chat.preview.rail_open')}
        >
          <LayoutGrid size={15} />
          {expandIcon}
        </button>
      </div>
    );
  }

  return (
    <div className={`chat-doc-preview-rail is-open is-${railSide}`} aria-label={tChat('chat.preview.rail')}>
      <div className="chat-doc-preview-rail-header">
        <span className="chat-doc-preview-rail-count">{source.count}</span>
        <button
          type="button"
          className="chat-doc-preview-rail-action"
          onClick={() => setRailSide(railSide === 'left' ? 'right' : 'left')}
          aria-label={tChat(railSide === 'left' ? 'chat.preview.rail_to_right' : 'chat.preview.rail_to_left')}
          title={tChat(railSide === 'left' ? 'chat.preview.rail_to_right' : 'chat.preview.rail_to_left')}
        >
          <Columns size={14} />
        </button>
        <button
          type="button"
          className="chat-doc-preview-rail-action"
          onClick={toggleRail}
          aria-expanded
          aria-label={tChat('chat.preview.rail_close')}
          title={tChat('chat.preview.rail_close')}
        >
          {collapseIcon}
        </button>
      </div>
      <div
        className="chat-doc-preview-rail-list scrollbar-thin"
        ref={listRef}
        onKeyDown={onListKeyDown}
        tabIndex={0}
        role="group"
        aria-label={tChat('chat.preview.rail_nav')}
      >
        {Array.from({ length: source.count }, (_, i) => (
          <ThumbItem key={`${source.key}:${i}`} index={i} active={i === activeIndex} />
        ))}
      </div>
    </div>
  );
}

/**
 * 썸네일 한 칸. 레일 안에 들어왔을 때만 그린다(IntersectionObserver) — 100장짜리 덱에서
 * 전부 그리면 렌더러가 오래 붙잡힌다. 화면 밖으로 나가도 이미 그린 것은 유지한다(재그리기 비용 ↑).
 */
function ThumbItem({ index, active }: { index: number; active: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const drawnRef = useRef(false);
  const source = usePreviewNavStore((s) => s.source);
  const goTo = source?.goTo;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !source) return;
    let cleanup: (() => void) | void;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || drawnRef.current) return;
        drawnRef.current = true;
        io.disconnect();
        // 렌더러가 죽은 뒤 호출되면 예외가 날 수 있다 — 썸네일 하나 때문에 패널이 깨지지 않게.
        try {
          cleanup = source.drawThumb(index, host);
        } catch {
          drawnRef.current = false;
        }
      },
      { root: host.closest('.chat-doc-preview-rail-list'), rootMargin: '160px' },
    );
    io.observe(host);
    return () => {
      io.disconnect();
      cleanup?.();
      drawnRef.current = false;
    };
  }, [index, source]);

  return (
    <button
      type="button"
      className={`chat-doc-preview-thumb${active ? ' is-active' : ''}`}
      onClick={() => goTo?.(index)}
      aria-label={tChat('chat.preview.go_to_page', { n: index + 1 })}
      aria-current={active || undefined}
      title={tChat('chat.preview.go_to_page', { n: index + 1 })}
    >
      <span className="chat-doc-preview-thumb-canvas" ref={hostRef} aria-hidden />
      <span className="chat-doc-preview-thumb-label">{index + 1}</span>
    </button>
  );
}
